/// Lazy-loaded confetti shower. The canvas-confetti library (~6KB gzipped)
/// is imported on first use only, so the initial kudos route bundle stays
/// lean (rule 2.4, dynamic imports for heavy components).
///
/// Colors are read from CSS custom properties at runtime so the palette stays
/// on-system (rule D1, no raw hex in product code). A safe fallback palette
/// kicks in for SSR/tests where `getComputedStyle` isn't usable.

type ConfettiModule = typeof import("canvas-confetti");
type ConfettiFn = ConfettiModule extends { default: infer F } ? F : ConfettiModule;
type ConfettiNamespace = ConfettiModule extends { default: infer F }
  ? F & ConfettiModule
  : ConfettiModule;

let confettiPromise: Promise<ConfettiFn> | null = null;
let cachedShapes: ReturnType<ConfettiNamespace["shapeFromPath"]>[] | null = null;

function loadConfetti(): Promise<ConfettiFn> {
  if (!confettiPromise) {
    confettiPromise = import("canvas-confetti").then((mod) => {
      const maybeDefault = (mod as { default?: ConfettiFn }).default;
      return (maybeDefault ?? (mod as unknown as ConfettiFn)) satisfies ConfettiFn;
    });
  }
  return confettiPromise;
}

// reason: SSR/test fallback; mirrors tokens.css brand hex values that
// canvas-confetti needs as raw strings when getComputedStyle is unusable.
const FALLBACK_PALETTE = ["#7c3aed", "#ec4899", "#a78bfa", "#f9a8d4", "#c4b5fd"];

/// Tight brand palette. Anchored on violet + pink with soft 300-weight
/// highlights for depth. Gold sparkle (`--pw-warning`) reserved for the big
/// shower so it stays special.
function brandPalette(includeSparkle: boolean): string[] {
  if (typeof window === "undefined") return FALLBACK_PALETTE;
  const styles = getComputedStyle(document.documentElement);
  const vars = [
    "--pw-violet-500",
    "--pw-violet-600",
    "--pw-violet-300",
    "--pw-pink-400",
    "--pw-pink-500",
    "--pw-pink-300",
  ];
  if (includeSparkle) vars.push("--pw-warning");
  const colors = vars.map((v) => styles.getPropertyValue(v).trim()).filter((c) => c.length > 0);
  return colors.length >= 3 ? colors : FALLBACK_PALETTE;
}

/// Mirror the ambient backdrop's shape vocabulary (square / circle /
/// triangle / four-point star) so the static and dynamic celebration layers
/// feel like one design system. Cached on first burst.
function getShapes(confetti: ConfettiFn): ReturnType<ConfettiNamespace["shapeFromPath"]>[] {
  if (cachedShapes) return cachedShapes;
  const ns = confetti as unknown as ConfettiNamespace;
  if (typeof ns.shapeFromPath !== "function") {
    cachedShapes = [];
    return cachedShapes;
  }
  cachedShapes = [
    // Rounded square card
    ns.shapeFromPath({
      path: "M2 0 L10 0 Q12 0 12 2 L12 10 Q12 12 10 12 L2 12 Q0 12 0 10 L0 2 Q0 0 2 0 Z",
    }),
    // Circle
    ns.shapeFromPath({ path: "M6 0 A6 6 0 1 0 6 12 A6 6 0 1 0 6 0 Z" }),
    // Triangle
    ns.shapeFromPath({ path: "M6 0 L12 12 L0 12 Z" }),
    // Four-point star
    ns.shapeFromPath({ path: "M6 0 L7.2 4.8 L12 6 L7.2 7.2 L6 12 L4.8 7.2 L0 6 L4.8 4.8 Z" }),
  ];
  return cachedShapes;
}

interface BurstOptions {
  /// Origin in viewport coordinates (0..1 on each axis). Defaults to center,
  /// slightly above middle so particles fall into view.
  origin?: { x: number; y: number };
  /// Roughly maps to "small" (clap) vs "big" (give). Defaults to "big".
  intensity?: "small" | "big";
}

function randDrift(): number {
  return (Math.random() - 0.5) * 0.6;
}

/// Fire a single confetti burst. Resolves once the engine has been loaded
/// and the burst has been queued. Safe to ignore the promise.
export async function fireConfetti(opts: BurstOptions = {}): Promise<void> {
  const confetti = await loadConfetti();
  const shapes = getShapes(confetti);
  const intensity = opts.intensity ?? "big";
  const origin = opts.origin ?? { x: 0.5, y: 0.42 };
  const colors = brandPalette(intensity === "big");

  // Shared physics, tuned for smooth and "real" motion: graceful gravity,
  // slow exponential decay, gentle horizontal drift, long ticks so the
  // fade tail reads without sudden pop-offs.
  const base = {
    colors,
    shapes: shapes.length > 0 ? shapes : undefined,
    gravity: 0.78,
    decay: 0.94,
    ticks: 320,
    disableForReducedMotion: true,
  } as const;

  if (intensity === "small") {
    confetti({
      ...base,
      particleCount: 40,
      spread: 60,
      startVelocity: 32,
      scalar: 1.25,
      drift: randDrift(),
      origin,
    });
    return;
  }

  // Big shower: center burst now, then left + right cannons stagger 120ms
  // apart. Each cannon fires two paired bursts at different scalars to
  // fake size variance, since canvas-confetti only accepts one scalar/call.
  confetti({
    ...base,
    particleCount: 55,
    spread: 75,
    startVelocity: 52,
    scalar: 1.8,
    drift: randDrift(),
    origin,
  });
  confetti({
    ...base,
    particleCount: 45,
    spread: 80,
    startVelocity: 58,
    scalar: 1.4,
    drift: randDrift(),
    origin,
  });

  window.setTimeout(() => {
    confetti({
      ...base,
      particleCount: 45,
      angle: 60,
      spread: 70,
      startVelocity: 60,
      scalar: 1.7,
      drift: randDrift(),
      origin: { x: 0, y: 0.78 },
    });
    confetti({
      ...base,
      particleCount: 35,
      angle: 60,
      spread: 75,
      startVelocity: 65,
      scalar: 1.3,
      drift: randDrift(),
      origin: { x: 0, y: 0.78 },
    });
  }, 120);

  window.setTimeout(() => {
    confetti({
      ...base,
      particleCount: 45,
      angle: 120,
      spread: 70,
      startVelocity: 60,
      scalar: 1.7,
      drift: randDrift(),
      origin: { x: 1, y: 0.78 },
    });
    confetti({
      ...base,
      particleCount: 35,
      angle: 120,
      spread: 75,
      startVelocity: 65,
      scalar: 1.3,
      drift: randDrift(),
      origin: { x: 1, y: 0.78 },
    });
  }, 240);
}

/// Preload the confetti engine ahead of the first burst. Call this from a
/// hover/focus handler on the Give Kudos button to hide the network/parse
/// cost behind user intent (rule 2.6, preload on intent).
export function preloadConfetti(): void {
  void loadConfetti();
}
